import React, { useState, useEffect } from 'react';
import { Trash2, User } from 'lucide-react';
import { useTheme } from './ThemeContext';
import SidePanel from './components/SidePanel';
import Icon from './components/Icon';
import { getCurrencyTextClass } from './utils/currencyColors';

/**
 * Компонент карточки клиента с детальной информацией (в боковой панели)
 * @param {Object} props - Пропсы компонента
 * @param {string} props.clientId - ID клиента для загрузки данных
 * @param {Function} props.onClose - Функция для закрытия карточки
 * @param {Function} props.fetchWithAuth - Функция для аутентифицированных запросов
 * @param {boolean} props.isOpen - Открыта ли панель
 * @param {Function} [props.onDeleted] - Вызов после успешного удаления клиента (обновить список)
 * @param {boolean} [props.canDelete] - Показывать ли кнопку удаления (для ADMIN)
 */
const ClientCard = ({ clientId, onClose, fetchWithAuth, isOpen = true, onDeleted = null, canDelete = false }) => {
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const { currentTheme, isDarkMode } = useTheme();

  // Функции для получения классов в зависимости от темы
  const getCardClass = () => {
    if (currentTheme === 'dark') return 'bg-gray-900 text-white';
    if (currentTheme === 'grither') return 'bg-white';
    if (currentTheme === 'custom') return 'universal-card';
    return 'bg-white';
  };

  const getTextClass = (type = 'primary') => {
    if (currentTheme === 'dark') {
      return type === 'primary' ? 'text-white' : 'text-gray-400';
    }
    if (currentTheme === 'grither') {
      return type === 'primary' ? 'text-grither-dark' : 'text-grither-secondary';
    }
    if (currentTheme === 'custom') {
      return type === 'primary' ? '' : '';
    }
    return type === 'primary' ? 'text-gray-900' : 'text-gray-600';
  };

  const getButtonClass = (variant = 'primary') => {
    if (currentTheme === 'dark') {
      return variant === 'primary' 
        ? 'bg-blue-600 hover:bg-blue-700 text-white'
        : 'bg-gray-700 hover:bg-gray-600 text-white';
    }
    if (currentTheme === 'grither') {
      return variant === 'primary'
        ? 'bg-grither-primary hover:bg-grither-primary-dark text-white'
        : 'bg-grither-gray-100 hover:bg-grither-gray-200 text-grither-dark';
    }
    if (currentTheme === 'custom') {
      return variant === 'primary' ? 'universal-primary-button' : 'universal-secondary-button';
    }
    return variant === 'primary'
      ? 'bg-blue-600 hover:bg-blue-700 text-white'
      : 'bg-gray-100 hover:bg-gray-200 text-gray-700';
  };

  // Загрузка данных клиента
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetchWithAuth(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}`
        );
        
        if (response && response.ok) {
          const data = await response.json();
          setClientData(data);
        } else {
          setError('Не удалось загрузить данные клиента');
        }
      } catch (err) {
        console.error('Error fetching client data:', err);
        setError('Ошибка при загрузке данных клиента');
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchClientData();
    }
  }, [clientId, fetchWithAuth]);

  // Форматирование чисел
  const formatNumber = (value) => {
    if (!value || value === '0' || value === 0) return '0';
    const num = typeof value === 'string' ? parseFloat(value.replace(/\s/g, '').replace(/,/g, '')) : value;
    return isNaN(num) ? '0' : Math.round(num).toLocaleString('ru-RU').replace(/,/g, ' ');
  };

  // Содержимое для загрузки
  const loadingContent = (
    <div className="text-center py-8">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className={`mt-4 ${getTextClass('secondary')}`}>Загрузка данных клиента...</p>
    </div>
  );

  // Содержимое для ошибки
  const errorContent = (
    <div className="text-center py-8">
      <p className="text-red-500 mb-4">{error || 'Данные клиента не найдены'}</p>
      <button
        onClick={onClose}
        className={`px-4 py-2 rounded-lg transition-colors ${getButtonClass('secondary')}`}
      >
        Закрыть
      </button>
    </div>
  );

  // Содержимое карточки клиента
  const clientContent = clientData && (
    <div className="space-y-4">
      {/* Контактная информация */}
      <div className={`rounded-lg p-4 ${
            currentTheme === 'dark' ? 'bg-gray-800' :
            currentTheme === 'grither' ? 'bg-grither-gray-50' :
            currentTheme === 'custom' ? 'universal-card' :
            'bg-gray-50'
          }`}>
        <h3 className={`text-base font-semibold mb-2 ${getTextClass('primary')}`}>
          <Icon emoji="📞" size={18} className="inline mr-1" /> Контактная информация
        </h3>
        <div className="space-y-2">
          <div>
            <p className={`text-xs ${getTextClass('secondary')}`}>Телефон</p>
            <p className={`font-medium ${getTextClass('primary')}`}>
              {clientData.phone || 'Не указан'}
            </p>
          </div>
          <div>
            <p className={`text-xs ${getTextClass('secondary')}`}>Telegram</p>
            <p className={`font-medium ${getTextClass('primary')}`}>
              {clientData.telegram_username ? `@${clientData.telegram_username}` : 'Не указан'}
            </p>
          </div>
          {clientData.source != null && clientData.source !== '' && (
            <div>
              <p className={`text-xs ${getTextClass('secondary')}`}>Источник</p>
              <p className={`font-medium ${getTextClass('primary')}`}>{clientData.source}</p>
            </div>
          )}
        </div>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-lg p-3 ${
          currentTheme === 'dark' ? 'bg-gray-800' :
          currentTheme === 'grither' ? 'bg-grither-gray-50' :
          currentTheme === 'custom' ? 'universal-card' :
          'bg-gray-50'
        }`}>
          <p className={`text-xs ${getTextClass('secondary')} mb-1`}>Всего сделок</p>
          <p className={`text-xl font-bold ${
            currentTheme === 'dark' ? 'text-blue-400' :
            currentTheme === 'grither' ? 'text-grither-primary' :
            currentTheme === 'custom' ? '' : 'text-blue-600'
          }`} style={currentTheme === 'custom' ? {
            color: 'var(--theme-primary-button, var(--primary-button, #3b82f6))'
          } : {}}>
            {clientData.total_deals}
          </p>
        </div>

        <div className={`rounded-lg p-3 ${
          currentTheme === 'dark' ? 'bg-gray-800' :
          currentTheme === 'grither' ? 'bg-grither-gray-50' :
          currentTheme === 'custom' ? 'universal-card' :
          'bg-gray-50'
        }`}>
          <p className={`text-xs ${getTextClass('secondary')} mb-1`}>Общая сумма</p>
          <p className={`text-lg font-bold ${
            currentTheme === 'dark' ? 'text-green-400' :
            currentTheme === 'grither' ? 'text-green-700' :
            currentTheme === 'custom' ? '' : 'text-green-600'
          }`} style={currentTheme === 'custom' ? {
            color: 'var(--theme-success-color, var(--success-color, #10b981))'
          } : {}}>
            {formatNumber(clientData.total_thb_amount)} ฿
          </p>
        </div>

        <div className={`rounded-lg p-3 ${
          currentTheme === 'dark' ? 'bg-gray-800' :
          currentTheme === 'grither' ? 'bg-grither-gray-50' :
          currentTheme === 'custom' ? 'universal-card' :
          'bg-gray-50'
        }`}>
          <p className={`text-xs ${getTextClass('secondary')} mb-1`}>Средняя сделка</p>
          <p className={`text-lg font-bold ${getTextClass('primary')}`}>
            {formatNumber(clientData.avg_deal_amount)} ฿
          </p>
        </div>

        <div className={`rounded-lg p-3 ${
          currentTheme === 'dark' ? 'bg-gray-800' :
          currentTheme === 'grither' ? 'bg-grither-gray-50' :
          currentTheme === 'custom' ? 'universal-card' :
          'bg-gray-50'
        }`}>
          <p className={`text-xs ${getTextClass('secondary')} mb-1`}>Последний контакт</p>
          <p className={`text-xs font-medium ${getTextClass('primary')}`}>
            {clientData.last_contact_date || 'Нет данных'}
          </p>
        </div>
      </div>

      {/* Даты */}
      <div className={`rounded-lg p-4 ${
        currentTheme === 'dark' ? 'bg-gray-800' :
        currentTheme === 'grither' ? 'bg-grither-gray-50' :
        currentTheme === 'custom' ? 'universal-card' :
        'bg-gray-50'
      }`}>
        <h3 className={`text-base font-semibold mb-2 flex items-center gap-2 ${getTextClass('primary')}`}>
          <Icon emoji="📅" size={18} className="flex-shrink-0" /> История
        </h3>
        <div className="space-y-2">
          <div>
            <p className={`text-xs ${getTextClass('secondary')}`}>Первый контакт</p>
            <p className={`text-sm font-medium ${getTextClass('primary')}`}>
              {clientData.first_contact_date || 'Нет данных'}
            </p>
          </div>
          <div>
            <p className={`text-xs ${getTextClass('secondary')}`}>Последний контакт</p>
            <p className={`text-sm font-medium ${getTextClass('primary')}`}>
              {clientData.last_contact_date || 'Нет данных'}
            </p>
          </div>
          {(clientData.created_by_username || clientData.created_by_role) && (
            <div>
              <p className={`text-xs ${getTextClass('secondary')}`}>Добавлен пользователем</p>
              <p className={`text-sm font-medium ${getTextClass('primary')}`}>
                {clientData.created_by_username || 'Не указан'}
                {clientData.created_by_role && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                    clientData.created_by_role === 'ADMIN' 
                      ? 'bg-purple-100 text-purple-700' 
                      : clientData.created_by_role === 'OPERATOR' 
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {clientData.created_by_role === 'ADMIN' ? 'Администратор' : 
                     clientData.created_by_role === 'OPERATOR' ? 'Оператор' :
                     clientData.created_by_role === 'MANAGER' ? 'Менеджер' :
                     clientData.created_by_role === 'CASHIER' ? 'Кассир' : clientData.created_by_role}
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Последние сделки */}
      {clientData.recent_deals && clientData.recent_deals.length > 0 && (
        <div className={`rounded-lg p-4 ${
          currentTheme === 'dark' ? 'bg-gray-800' :
          currentTheme === 'grither' ? 'bg-grither-gray-50' :
          currentTheme === 'custom' ? 'universal-card' :
          'bg-gray-50'
        }`}>
          <h3 className={`text-base font-semibold mb-2 ${getTextClass('primary')}`}>
            <Icon emoji="💼" size={18} className="inline mr-1" /> Последние сделки
          </h3>
          <div className="space-y-2">
            {clientData.recent_deals.map((deal, index) => (
              <div 
                key={index}
                className={`p-2 rounded-lg border ${
                  currentTheme === 'dark' ? 'border-gray-700 bg-gray-900' :
                  currentTheme === 'grither' ? 'border-grither-gray-200 bg-white' :
                  currentTheme === 'custom' ? 'universal-card' :
                  'border-gray-200 bg-white'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-1">
                      <span className={`text-sm font-semibold ${getTextClass('primary')}`}>
                        #{deal.deal_id}
                      </span>
                      <span className={`text-xs ${getTextClass('secondary')}`}>
                        {deal.date}
                      </span>
                    </div>
                    <div className={`text-xs ${getTextClass('secondary')} mt-0.5`}>
                      <span className={getCurrencyTextClass(deal.currency, currentTheme === 'dark')}>{deal.currency}</span> • ₽{parseFloat(deal.rate || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-right ml-2">
                    <div className={`text-sm font-bold ${
                      currentTheme === 'dark' ? 'text-green-400' :
                      currentTheme === 'grither' ? 'text-green-700' :
                      currentTheme === 'custom' ? '' : 'text-green-600'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-success-color, var(--success-color, #10b981))'
                    } : {}}>
                      {formatNumber(deal.amount_thb)} ฿
                    </div>
                    <div className={`text-xs ${getTextClass('secondary')}`}>
                      {formatNumber(deal.amount)} <span className={getCurrencyTextClass(deal.currency, currentTheme === 'dark')}>{deal.currency}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Заметки */}
      {clientData.notes && (
        <div className={`rounded-lg p-4 ${
          currentTheme === 'dark' ? 'bg-gray-800' :
          currentTheme === 'grither' ? 'bg-grither-gray-50' :
          currentTheme === 'custom' ? 'universal-card' :
          'bg-gray-50'
        }`}>
          <h3 className={`text-base font-semibold mb-2 ${getTextClass('primary')}`}>
            <Icon emoji="📝" size={18} className="inline mr-1" /> Заметки
          </h3>
          <p className={`text-sm ${getTextClass('secondary')}`}>
            {clientData.notes}
          </p>
        </div>
      )}
    </div>
  );

  const handleDeleteClient = async () => {
    if (!clientId) return;
    setDeleting(true);
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}`,
        { method: 'DELETE' }
      );
      if (response && response.ok) {
        setShowDeleteConfirm(false);
        if (typeof onDeleted === 'function') onDeleted();
        onClose();
      } else {
        const err = response ? await response.json().catch(() => ({})) : {};
        alert(err.detail || 'Не удалось удалить клиента');
      }
    } catch (e) {
      console.error(e);
      alert('Ошибка при удалении клиента');
    } finally {
      setDeleting(false);
    }
  };

  // Модальное окно предупреждения перед удалением (z-index выше SidePanel 1000)
  const deleteConfirmModal = showDeleteConfirm && (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100 }}
      onClick={() => setShowDeleteConfirm(false)}
    >
      <div
        className={`max-w-md w-full rounded-xl shadow-xl p-6 ${
          currentTheme === 'dark' ? 'bg-gray-800' :
          currentTheme === 'grither' ? 'bg-white grither-shadow' :
          'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
            currentTheme === 'dark' ? 'bg-red-500/20' : 'bg-red-100'
          }`}>
            <Trash2 size={24} className="text-red-600" />
          </div>
          <h3 className={`text-lg font-semibold ${getTextClass('primary')}`}>
            Удалить клиента?
          </h3>
        </div>
        <p className={`text-sm ${getTextClass('secondary')} mb-6`}>
          Вы уверены, что хотите удалить клиента <strong>«{clientData?.name || clientId}»</strong> и все его сделки? Это действие нельзя отменить.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className={`px-4 py-2 rounded-lg transition-colors ${getButtonClass('secondary')}`}
          >
            Отмена
          </button>
          <button
            onClick={handleDeleteClient}
            disabled={deleting}
            className={`px-4 py-2 rounded-lg transition-colors ${
              currentTheme === 'dark' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'
            } disabled:opacity-50`}
          >
            {deleting ? 'Удаление…' : 'Удалить'}
          </button>
        </div>
      </div>
    </div>
  );

  // Футер: иконка удаления слева, кнопка «Закрыть» справа
  const footerContent = (
    <div className="flex justify-between items-center">
      <div>
        {canDelete && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            className={`p-2 rounded-lg transition-colors ${
              currentTheme === 'dark' ? 'bg-red-500/40 hover:bg-red-500/60 text-white' : 'bg-red-100 hover:bg-red-200 text-red-700'
            } disabled:opacity-50`}
            title="Удалить клиента и его сделки"
          >
            <Trash2 size={20} strokeWidth={2} />
          </button>
        )}
      </div>
      <button
        onClick={onClose}
        className={`px-4 py-2 rounded-lg transition-colors ${getButtonClass('primary')}`}
      >
        Закрыть
      </button>
    </div>
  );

  return (
    <>
      {deleteConfirmModal}
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        title={clientData ? clientData.name : 'Клиент'}
        titleIcon={clientData ? <User size={22} strokeWidth={2} /> : null}
        width="450px"
        footer={!loading && !error ? footerContent : null}
        hideCloseButton
      >
        {loading ? loadingContent : error || !clientData ? errorContent : clientContent}
      </SidePanel>
    </>
  );
};

export default ClientCard;
